import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ReactElement } from 'react'
import { api } from '../../api'
import { RequireSession } from '../../auth'
import { Betrag } from '../../components/Betrag'
import { Tabelle } from '../../components/Tabelle'
import { TabellenZustand } from '../../components/Zustand'
import { datum } from '../../format'

/** Eine Zeile aus `myPipeline` (src/worker/services/reads.ts, MyPipelineRow). */
export interface MeinDeal {
  id: string
  tenant_id: string
  tenant_name: string
  customer_name: string
  hl_contact_id: string | null
  status: string
  signed_at: string | null
  contract_value_cents: number | null
  product_code: string | null
  gezahlt_cents: number
  letzter_zahlungseingang: string | null
  meine_rollen: string[]
}

/** `DealStatus` aus src/worker/engine/types.ts; `storno` und `ignorieren` filtert der Server weg. */
export const DEAL_STATUS: Readonly<Record<string, string>> = {
  aktiv: 'Aktiv',
  weiterlauf: 'Weiterlauf',
  beendet: 'Beendet',
  pruefen: 'Prüfen',
  storno: 'Storniert',
  ignorieren: 'Ignoriert',
}

export const ROLLE: Readonly<Record<string, string>> = {
  closer: 'Closer',
  setter: 'Setter',
  host: 'Empfehlungsgeber',
  sonstige: 'Sonstige Rolle',
}

export function rollenText(rollen: readonly string[]): string {
  return rollen.map((rolle) => ROLLE[rolle] ?? rolle).join(', ')
}

/** Die Anzahl der `<th>` in der Kopfzeile — das Skelett muss dieselbe Breite haben. */
const SPALTEN = 9

export function PipelineScreen(): ReactElement {
  const pipeline = useQuery({
    queryKey: ['me', 'pipeline'],
    queryFn: () => api.get<{ pipeline: MeinDeal[] }>('/api/me/pipeline'),
  })
  const deals = pipeline.data?.pipeline ?? []

  return (
    <div className="seite">
      <h1>Meine Pipeline</h1>

      {/*
        Der Satz nennt „Gezahlt" bewusst eine Summe der Eingänge und nicht das, was der Kunde netto
        gezahlt hat: `myPipeline` (src/worker/services/reads.ts) summiert nur `payment_deltas` mit
        `kind = 'payment'`, und eine Rückerstattung steht als eigene Zeile mit `kind = 'refund'`
        daneben. Nach einer Rückerstattung wäre „was der Kunde tatsächlich gezahlt hat" also zu
        hoch. Nachgerechnet wird hier nichts — der Bildschirm sagt, was die Zahl ist.
      */}
      <p className="mb-8 mt-2 max-w-prose text-[var(--ink-muted)]">
        Alle Abschlüsse, bei denen Sie als Closer, Setter oder Empfehlungsgeber eingetragen sind. Hier stehen
        keine erwarteten Provisionen, sondern die Summe der Zahlungseingänge; Rückerstattungen sind
        darin nicht abgezogen, und „Letzter Zahlungseingang" nennt den letzten Eingang, keine
        Rückerstattung. Ihre Provision entsteht mit jedem Zahlungseingang und erscheint dann unter
        „Meine Provisionen".
      </p>

      {/*
        Laden, Fehler und Leere stehen im Tabellenkörper und nicht daneben: die Kopfzeile bleibt
        damit über alle drei Zustände stehen, das Skelett hat exakt die Spaltenzahl der fertigen
        Tabelle, und beim Eintreffen der Daten springt nichts. Angesagt wird jeweils nur ein
        Zustand — die drei Bedingungen schließen einander aus.
      */}
      <Tabelle
        beschriftung="Abschlüsse, an denen ich beteiligt bin"
        kopf={
          <tr>
            <th scope="col">Auftraggeber</th>
            <th scope="col">Kunde</th>
            <th scope="col">Produkt</th>
            <th scope="col">Status</th>
            <th scope="col">Meine Rolle</th>
            <th scope="col">Vertrag vom</th>
            <th scope="col" className="zahl">Vertragswert</th>
            <th scope="col" className="zahl">Gezahlt</th>
            <th scope="col">Letzter Zahlungseingang</th>
          </tr>
        }
      >
        {pipeline.isPending && (
          <TabellenZustand art="laedt" spalten={SPALTEN} beschriftung="Ihre Abschlüsse werden geladen." />
        )}
        {pipeline.isError && (
          <TabellenZustand
            art="fehler"
            spalten={SPALTEN}
            fehler={pipeline.error}
            onWiederholen={() => void pipeline.refetch()}
          />
        )}
        {!pipeline.isPending && !pipeline.isError && deals.length === 0 && (
          <TabellenZustand
            art="leer"
            spalten={SPALTEN}
            titel="Keine laufenden Abschlüsse"
            hinweis="Hier stehen die Abschlüsse, bei denen Sie als Closer, Setter oder Empfehlungsgeber eingetragen sind. Fehlt einer, wenden Sie sich an Ihren Auftraggeber — die Rollen pflegt er in den Abschlüssen."
          />
        )}
        {deals.map((deal) => (
          <tr key={deal.id}>
            <td>{deal.tenant_name}</td>
            <td>{deal.customer_name}</td>
            <td>{deal.product_code ?? '—'}</td>
            <td>{DEAL_STATUS[deal.status] ?? deal.status}</td>
            <td>{rollenText(deal.meine_rollen)}</td>
            <td>{deal.signed_at === null ? '—' : datum(deal.signed_at)}</td>
            <td className="zahl">
              {deal.contract_value_cents === null ? '—' : <Betrag cents={deal.contract_value_cents} />}
            </td>
            <td className="zahl"><Betrag cents={deal.gezahlt_cents} /></td>
            <td>{deal.letzter_zahlungseingang === null ? '—' : datum(deal.letzter_zahlungseingang)}</td>
          </tr>
        ))}
      </Tabelle>
    </div>
  )
}

/**
 * Der Zugangsschutz steht hier und nicht im Bildschirm: `GET /api/me/pipeline` verlangt eine
 * Sitzung, und ohne sie soll die Anmeldung erscheinen und nicht ein 401 als Fehlerzeile
 * (dieselbe Aufteilung wie in `me/provisionen.tsx`).
 */
function PipelineRoute(): ReactElement {
  return (
    <RequireSession>
      <PipelineScreen />
    </RequireSession>
  )
}

export const Route = createFileRoute('/me/pipeline')({ component: PipelineRoute })
